import type { ProcessListItemDto } from '@process-hub/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { sharePointNote } from '../sharepoint';
import { StatusPill } from '../components/Badges';
import { describeError } from '../errors';

/**
 * Admin: who owns what, and reassignment (D-029). Owner-less processes come first. The new
 * owner is looked up in Entra by work email; the API insists on an enabled account in the
 * department's group and restores an Orphaned process to the status it had.
 */
export function OwnersCard({ onNotice }: { onNotice: (m: string) => void }) {
  const [items, setItems] = useState<ProcessListItemDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    api
      .listProcesses(true)
      .then((p) => setItems([...p].sort((a, b) => Number(b.status === 'Orphaned') - Number(a.status === 'Orphaned') || a.departmentName.localeCompare(b.departmentName) || a.name.localeCompare(b.name))))
      .catch((e: unknown) => setError(describeError(e)));
  }, [version]);

  const orphaned = items?.filter((p) => p.status === 'Orphaned').length ?? 0;
  return (
    <div className="card">
      <div className="card__header">
        <div className="card__title">Process owners</div>
        <div className="card__meta">{items ? `${items.length} processes · ${orphaned} owner-less` : 'Loading.'}</div>
      </div>
      {error && <div className="notice notice--error" style={{ margin: 18 }}>{error}</div>}
      {items && items.length === 0 && (
        <div className="notice" style={{ margin: 18 }}>
          No processes yet.
        </div>
      )}
      {items && items.length > 0 && (
        <table className="grid">
          <thead>
            <tr>
              <th>Process</th>
              <th>Department</th>
              <th>Status</th>
              <th>Owner</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <OwnerRow key={p.id} p={p} onChanged={() => setVersion((v) => v + 1)} onNotice={onNotice} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function OwnerRow({ p, onChanged, onNotice }: { p: ProcessListItemDto; onChanged: () => void; onNotice: (m: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [user, setUser] = useState('');
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setFieldError(null);
    try {
      const r = await api.reassignOwner(p.slug, user.trim());
      onNotice(`${p.name}: ${r.ownerName} is now the owner${r.previousStatus === 'Orphaned' ? ` and the process is back to ${r.status}` : ''}.${sharePointNote(r.sharePoint)}`);
      setEditing(false);
      setUser('');
      onChanged();
    } catch (e) {
      setFieldError(describeError(e, 'The owner could not be changed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className={p.status === 'Orphaned' ? 'grid__row--attention' : undefined}>
      <td className="grid__primary">
        <Link to={`/processes/${p.slug}`}>{p.name}</Link>
      </td>
      <td>{p.departmentName}</td>
      <td>
        <StatusPill status={p.status} />
      </td>
      <td>
        {editing ? (
          <div className="field" style={{ margin: 0 }}>
            <input aria-label={`New owner for ${p.name}`} type="email" value={user} onChange={(e) => setUser(e.target.value)} placeholder="name@weavertech.us" style={{ width: 260 }} />
            {fieldError && <div className="issue issue--error">{fieldError}</div>}
          </div>
        ) : (
          (p.ownerName ?? <span className="muted">unknown</span>)
        )}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {editing ? (
          <>
            <button type="button" className="button" disabled={busy || !user.includes('@')} onClick={save}>
              Assign
            </button>{' '}
            <button type="button" className="button button--secondary" disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </>
        ) : (
          <button type="button" className={`button ${p.status === 'Orphaned' ? '' : 'button--secondary'}`} onClick={() => setEditing(true)}>
            {p.status === 'Orphaned' ? 'Assign owner' : 'Reassign'}
          </button>
        )}
      </td>
    </tr>
  );
}
